import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '~/lib/cn';
import { currencyFlagSrc } from '~/lib/currency-flag';
import { useContent } from '~/lib/useLanguage';
import {
  FALLBACK_RATES,
  RECEIVE_CURRENCIES,
  getQuote,
  type PricingTier,
  type Quote,
  type ReceiveCurrency,
} from '~/lib/quote';

/*
 * Native port of the arrow-dev "Send money" calculator (apps/web calculator page).
 *
 * DATA SOURCE — live quotes come from the nsave backend, proxied through our own
 * server so the browser never calls `api.nsave.com` directly (the upstream only
 * allows nsave.com origins, so a direct client fetch is blocked by CORS):
 *   `getQuote` (src/lib/quote.ts, a TanStack Start `createServerFn`) runs on our
 *   server and fetches:
 *     GET `${FX_API_BASE_URL}/transactions/fx/rate/public`
 *         ?sourceCurrency=USD&targetCurrency=EGP&amount=1000&pricingTier=PRO
 *     -> { exchangeRate, fee, amount }
 *   and returns the normalized `{ rate, fee, amount }` to the client.
 *
 * We fetch (debounced) whenever the amount or target currency changes, prefer the
 * recipient `amount` the API returns (it already accounts for the fee), and fall
 * back to the static PRO-tier constants below whenever the upstream is unreachable
 * so the card never shows a broken/empty state.
 *
 * Layout follows Figma node 170:1382 (360×508 card).
 */

/** Flat PRO-tier fee (USD) — fallback when the live upstream is unreachable. */
const FEE_USD = 1;

/** The card's fixed Figma design width (360×508 — see the layout note further down; the
 * `aspect-[360/508]` utility on the outer frame encodes the same ratio literally, since
 * Tailwind's class scanner needs a static string rather than an interpolated constant). */
const CARD_W = 360;

/** Smallest common phone viewport width — used as the safe default size before the actual
 * container width has been measured (see the `scale` state further down). */
const NARROWEST_PHONE_W = 320;

/** "You send" options. */
const SEND_OPTIONS = ['USD', 'EUR', 'GBP'] as const;

/** "Recipient gets" options — our supported payout currencies. */
const RECEIVE_OPTIONS = RECEIVE_CURRENCIES;

/** Plan picker options. Value maps to the upstream `pricingTier`. */
/** Order of the rows in the in-card "Select plan" panel. Labels come from the
 *  active language bundle (`ui.calculator.plans`). */
const PLAN_OPTIONS: { value: PricingTier }[] = [{ value: 'PRO' }, { value: 'FREE' }];

function fmtRate(rate: number): string {
  if (!Number.isFinite(rate)) return '-';
  return rate.toFixed(2);
}

function fmtMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function fmtRecipientAmount(amount: number): string {
  if (!Number.isFinite(amount)) return '';
  const sign = amount < 0 ? '-' : '';
  const [intPart, decPart] = Math.abs(amount).toFixed(2).split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${withCommas}.${decPart}`;
}

/**
 * Client-side estimate used before the first live quote and if the upstream fails.
 * `FALLBACK_RATES`/`FEE_USD` are USD-only static estimates — only valid for a
 * USD send. There's no reliable non-USD fallback, so EUR/GBP simply keep
 * showing the loading state on upstream failure rather than a guessed number.
 */
function fallbackQuote(target: ReceiveCurrency, amount: number): Quote {
  const rate = FALLBACK_RATES[target];
  return { rate, fee: FEE_USD, amount: (amount - FEE_USD) * rate };
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M8 6l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronLeft({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M12 6l-4 4 4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** In-field white pill: circular flag + code + chevron (Figma frame 170:1388). */
function CurrencyChip({
  code,
  label,
  onClick,
}: {
  code: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label} ${code}`}
      className="flex shrink-0 items-center gap-2 rounded-full bg-white p-2 transition-opacity hover:opacity-70">
      <img src={currencyFlagSrc(code)} alt="" className="size-6 rounded-full object-cover" />
      <span className="text-body font-semibold text-ink">{code}</span>
      <ChevronRight className="size-4 text-grey-500" />
    </button>
  );
}

/** Currency row (flag + code) shown in the in-card "Select currency" panel. */
function OptionRow({
  code,
  selected,
  onSelect,
}: {
  code: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-4 rounded-full px-3 py-[10px] text-left transition-colors',
        selected ? 'bg-black-6' : 'hover:bg-grey-50',
      )}>
      <img
        src={currencyFlagSrc(code)}
        alt=""
        aria-hidden="true"
        className="size-8 shrink-0 rounded-full object-cover"
      />
      <span className="truncate text-body font-semibold text-ink">{code}</span>
      {/* Fixed-size slot reserved in every state so the hover pill matches the
          selected pill's height exactly (checkmark only shown when selected). */}
      <span className="ml-auto flex size-6 shrink-0 items-center justify-center">
        {selected && (
          <img src="/images/calculator/selected.svg" alt="" aria-hidden="true" className="size-6" />
        )}
      </span>
    </button>
  );
}

/** Plan row (label + checkmark, no flag) shown in the in-card "Select plan" panel. */
function PlanRow({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-4 rounded-full px-3 py-[10px] text-left transition-colors',
        selected ? 'bg-black-6' : 'hover:bg-grey-50',
      )}>
      <span className="truncate text-body font-semibold text-ink">{label}</span>
      {/* Fixed-size slot reserved in every state so the hover pill matches the
          selected pill's height exactly (checkmark only shown when selected). */}
      <span className="ml-auto flex size-6 shrink-0 items-center justify-center">
        {selected && (
          <img src="/images/calculator/selected.svg" alt="" aria-hidden="true" className="size-6" />
        )}
      </span>
    </button>
  );
}

/**
 * Currency pair state can be controlled by a parent (so the rates strip can drive
 * the calculator) or left uncontrolled for standalone use. When `target`/`base`
 * props are supplied the parent owns the state via the `on*Change` callbacks.
 */
export function CurrencyCalculator({
  base: baseProp,
  target: targetProp,
  amount: amountProp,
  animateIn = true,
  plan: planProp,
  onBaseChange,
  onTargetChange,
  onAmountChange,
  onPlanChange,
}: {
  base?: string;
  target?: ReceiveCurrency;
  amount?: string;
  /**
   * Play the card's entrance animation (a grow from a small circle plus a
   * delayed body fade, see calculator.css).
   *
   * The `.calc-card` class itself stays on regardless — it also scopes the
   * `text-box-trim` override that stops descenders clipping inside this fixed
   * overflow-hidden card. Only the animation is opted out of, via
   * `.calc-card--no-intro`.
   *
   * The animation is choreographed against the homepage hero's own load
   * sequence, which is the only place that sequence exists. On the send-money
   * and calculator pages the card is the first thing in view with nothing to
   * choreograph against, so it just delays the numbers the visitor came for.
   */
  animateIn?: boolean;
  plan?: PricingTier;
  onBaseChange?: (code: string) => void;
  onTargetChange?: (code: ReceiveCurrency) => void;
  onAmountChange?: (value: string) => void;
  onPlanChange?: (plan: PricingTier) => void;
} = {}) {
  const [baseState, setBaseState] = useState('USD');
  const [targetState, setTargetState] = useState<ReceiveCurrency>('EGP');
  const [amountState, setAmountState] = useState('1000');
  const [planState, setPlanState] = useState<PricingTier>('PRO');
  const base = baseProp ?? baseState;
  const target = targetProp ?? targetState;
  const amount = amountProp ?? amountState;
  const plan = planProp ?? planState;
  const setBase = onBaseChange ?? setBaseState;
  const setTarget: (code: ReceiveCurrency) => void = onTargetChange ?? setTargetState;
  const setAmount = onAmountChange ?? setAmountState;
  const setPlan: (plan: PricingTier) => void = onPlanChange ?? setPlanState;

  // Which field opened the in-card picker (null = main view is shown).
  const [picker, setPicker] = useState<'source' | 'target' | 'plan' | null>(null);

  // Labels come from the active language bundle. The card keeps `dir="ltr"` by
  // design — its icons, row order and numeric formatting are the same in Arabic
  // — but the copy inside it still has to be translated.
  const { ui } = useContent();
  const t = ui.calculator;
  const backRef = useRef<HTMLButtonElement>(null);

  // The card's internal layout is a pixel-precise 360×508 Figma design (fixed offsets
  // throughout), so it doesn't reflow gracefully at narrower widths. Instead of reflowing it,
  // scale the whole thing down as one unit to fit whatever width its container actually has
  // (e.g. a phone viewport narrower than 360px) — `frameRef`'s own box is sized responsively via
  // CSS (aspect-ratio), and we just measure it to compute how much to shrink the fixed-size card
  // inside it.
  const frameRef = useRef<HTMLDivElement>(null);
  // On the send-money/pricing pages this card is server-rendered, so whatever we pick here is
  // what actually paints before the client can measure anything. Default to the narrowest
  // realistic phone width rather than the full design width, so a slow-to-hydrate page shows a
  // slightly-too-small card for a moment instead of a clipped, too-big one.
  const [scale, setScale] = useState(() => Math.min(1, NARROWEST_PHONE_W / CARD_W));

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const update = (width: number) => {
      if (width > 0) setScale(Math.min(1, width / CARD_W));
    };
    update(frame.getBoundingClientRect().width);

    // Missing on old browsers/embedded webviews — fall back to the safe default size above
    // rather than throwing.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => update(entry.contentRect.width));
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const numericAmount = Number.parseFloat(amount || '0');
  const hasAmount = numericAmount > 0 && Number.isFinite(numericAmount);

  // Cache the live quote keyed by the inputs it was computed for. Deriving the
  // displayed quote from this key (rather than clearing state in the effect)
  // keeps stale responses from ever showing and avoids synchronous setState in
  // the effect body.
  const inputKey = `${base}:${target}:${numericAmount}:${plan}`;
  const [result, setResult] = useState<{ key: string; quote: Quote } | null>(null);
  const quote = hasAmount && result?.key === inputKey ? result.quote : null;

  // Fetch a live quote (debounced) whenever the source, amount, target, or plan changes.
  useEffect(() => {
    if (!hasAmount) return;
    const key = inputKey;

    const timer = setTimeout(() => {
      void getQuote({
        data: {
          sourceCurrency: base,
          targetCurrency: target,
          amount: numericAmount,
          pricingTier: plan,
        },
      })
        .then((live) => {
          setResult({ key, quote: live });
        })
        .catch(() => {
          // Upstream unreachable. For USD we have a static estimate so the
          // card never shows a broken/empty state; there's no reliable
          // EUR/GBP equivalent, so leave those showing the loading state
          // rather than a guessed number.
          if (base === 'USD') {
            setResult({ key, quote: fallbackQuote(target, numericAmount) });
          }
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [inputKey, base, target, numericAmount, plan, hasAmount]);

  const open = picker !== null;
  const options: readonly string[] = picker === 'source' ? SEND_OPTIONS : RECEIVE_OPTIONS;
  const selectedCode = picker === 'source' ? base : target;

  // Move focus into the panel when it opens so it's keyboard reachable.
  useEffect(() => {
    if (open) backRef.current?.focus({ preventScroll: true });
  }, [open]);

  const handleSelect = (code: string) => {
    if (picker === 'source') {
      setBase(code);
    } else {
      setTarget(code as ReceiveCurrency);
    }
    setPicker(null);
  };

  return (
    // Outer frame reserves the calculator's responsive footprint (its own box scales with
    // its container via `aspect-ratio`, capped at the design width) from the very first frame,
    // so surrounding content never shifts. `pointer-events-auto` re-enables interaction when
    // this sits inside a `pointer-events-none` overlay (e.g. the Hero's click-through layer).
    // `overflow-hidden` is a safety net for the one frame before the layout effect above
    // measures the real width and corrects `scale` from its default of 1.
    <div
      ref={frameRef}
      className="pointer-events-auto mx-auto aspect-[360/508] w-full max-w-[360px] overflow-hidden">
      {/* This wrapper applies the responsive scale as a plain transform, kept on a separate
          element from `.calc-card` below so it doesn't fight with that element's own entrance
          animation (also a `transform`, see calculator.css) — the two transforms compose
          correctly stacked on parent/child rather than colliding on one element. */}
      <div className="origin-top-left" style={{ width: CARD_W, transform: `scale(${scale})` }}>
        {/* The card stays at its final 360×508 size/position the whole time; the
          entrance is a composited clip-path reveal (see .calc-card in global.css)
          that grows from a small centred circle, so there's no layout shift or
          reflow. Inner content fades in near the end so it never pops while the
          clip window is still small. */}
        {/* dir="ltr" pins the whole card to a fixed layout direction — this widget's
            icons, row order, slide transitions, and numeric/currency text (e.g. the
            rate line below) are the same in Arabic as in English by design, so it
            must not inherit `dir="rtl"` from the surrounding Arabic page. */}
        <div
          dir="ltr"
          className={cn(
            'calc-card relative h-[508px] w-[360px] overflow-hidden rounded-[40px] bg-white',
            !animateIn && 'calc-card--no-intro',
          )}>
          <div className="calc-card__body">
            {/* MAIN view — slides out to the left when the picker opens. */}
            <div
              inert={open}
              className={cn(
                'absolute inset-0 flex flex-col px-6 pt-[5px] transition-transform duration-300 ease-nav motion-reduce:transition-none',
                open ? '-translate-x-full' : 'translate-x-0',
              )}>
              {/* Field block: You send + Recipient gets, split by a thin divider. */}
              <div className="-mx-[19px] flex h-[217px] flex-col rounded-[32px] bg-grey-25 px-[19px]">
                <div className="flex flex-1 items-center justify-between gap-3">
                  <label className="flex min-w-0 flex-col gap-3">
                    <span className="text-body-m text-grey-500">{t.youSend}</span>
                    <input
                      type="number"
                      min={0}
                      inputMode="decimal"
                      placeholder={t.amountPlaceholder}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      aria-label={t.amountLabel}
                      className="w-full bg-transparent py-0 text-h4 font-semibold text-ink outline-none [text-box-trim:none] placeholder:text-grey-500 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </label>
                  <CurrencyChip
                    code={base}
                    label={t.changeCurrency}
                    onClick={() => setPicker('source')}
                  />
                </div>

                <div className="h-px bg-black-6" />

                <div className="flex flex-1 items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-3">
                    <span className="text-body-m text-grey-500">{t.recipientGets}</span>
                    {quote ? (
                      <output className="truncate text-h4 font-semibold text-ink [text-box-trim:none]">
                        {quote.amount < 0 ? (
                          <span className="text-body font-medium text-grey-500">
                            {t.amountTooLow}
                          </span>
                        ) : (
                          fmtRecipientAmount(quote.amount)
                        )}
                      </output>
                    ) : (
                      <span
                        className="block h-5 w-[76px] rounded-md bg-grey-300"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <CurrencyChip
                    code={target}
                    label={t.changeCurrency}
                    onClick={() => setPicker('target')}
                  />
                </div>
              </div>

              {/* Rate + fees (17px label left, 17px semibold value right). */}
              <dl className="mt-[38px] flex flex-col gap-[30px]">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-body text-grey-500">{t.exchangeRate}</dt>
                  <dd className="text-body font-semibold text-ink">
                    {quote ? `1 ${base} ~ ${fmtRate(quote.rate)} ${target}` : '···'}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-body text-grey-500">{t.fees}</dt>
                  <dd className="text-body font-semibold text-ink">
                    {quote ? fmtMoney(quote.fee, base) : '···'}
                  </dd>
                </div>
              </dl>

              {/* Plan control — opens the same Pro/Free slide-panel picker. */}
              <button
                type="button"
                onClick={() => setPicker('plan')}
                aria-label={`${t.changePlan} ${t.plans[plan]}`}
                className="mt-[124px] flex items-center justify-center gap-1 text-body font-medium text-red-500 transition-opacity hover:opacity-70">
                {t.plans[plan]}
                <ChevronRight className="size-5" />
              </button>
            </div>

            {/* SELECT CURRENCY panel — slides in from the right within the same card. */}
            <div
              inert={!open}
              className={cn(
                'absolute inset-0 flex flex-col bg-white transition-transform duration-300 ease-nav motion-reduce:transition-none',
                open ? 'translate-x-0' : 'translate-x-full',
              )}>
              <div className="relative px-6 pt-6">
                <button
                  ref={backRef}
                  type="button"
                  onClick={() => setPicker(null)}
                  aria-label={t.back}
                  className="absolute top-6 left-6 grid size-10 place-items-center rounded-full bg-grey-50 text-ink transition-colors hover:bg-grey-100">
                  <ChevronLeft className="size-6" />
                </button>
                <p className="flex h-10 items-center justify-center text-h5 font-semibold text-ink">
                  {picker === 'plan' ? t.selectPlan : t.selectCurrency}
                </p>
              </div>
              <ul className="mt-4 flex-1 space-y-3 overflow-y-auto px-6 pb-4">
                {picker === 'plan'
                  ? PLAN_OPTIONS.map((option) => (
                      <li key={option.value}>
                        <PlanRow
                          label={t.plans[option.value]}
                          selected={option.value === plan}
                          onSelect={() => {
                            setPlan(option.value);
                            setPicker(null);
                          }}
                        />
                      </li>
                    ))
                  : options.map((code) => (
                      <li key={code}>
                        <OptionRow
                          code={code}
                          selected={code === selectedCode}
                          onSelect={() => handleSelect(code)}
                        />
                      </li>
                    ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
